import React, { useState } from 'react';
import styled from 'styled-components';
import { Avatar, Badge, Button, SlideOver, Text, useToast } from '@teals/ui-web';
import type { Member } from '../../store/api/portalApi';
import {
  useAddTeamMemberMutation,
  useGetTeamsQuery,
  useRemoveTeamMemberMutation,
  useUpdateTeamMemberMutation,
} from '../../store/api/teamsApi';
import type { TeamRole } from '../../store/api/teamsApi';

function errorMessage(error: unknown): string {
  const data = (error as { data?: { message?: unknown } })?.data;
  return typeof data?.message === 'string' ? data.message : 'Something went wrong. Please try again.';
}

export function memberDisplayName(member: Member): string {
  const full = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  return full || member.email || member.userId;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RowText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const RoleChips = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Select = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.8125rem;
`;

const Note = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--error);
  font-size: 0.8125rem;
`;

interface MemberDrawerProps {
  orgId: string;
  member: Member | null;
  onClose: () => void;
}

/** A member's teams in this organisation only; other organisations are never shown. */
const MemberDrawer: React.FC<MemberDrawerProps> = ({ orgId, member, onClose }) => {
  const { show } = useToast();
  const { data: teams = [] } = useGetTeamsQuery(orgId, { skip: !member });
  const [addTeamMember, { isLoading: adding }] = useAddTeamMemberMutation();
  const [updateTeamMember] = useUpdateTeamMemberMutation();
  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const [addTeamId, setAddTeamId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const memberTeams = member?.teams || [];
  const joined = new Set(memberTeams.map((t) => t.teamId));
  const joinable = teams.filter((t) => !t.isDefault && !joined.has(t.id));
  const name = member ? memberDisplayName(member) : 'Member';

  const run = async (action: () => Promise<unknown>, success: string) => {
    setError(null);
    try {
      await action();
      show({ message: success, type: 'success' });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const close = () => {
    setAddTeamId('');
    setError(null);
    onClose();
  };

  return (
    <SlideOver isOpen={!!member} onClose={close} title={name} width="md">
      {member && (
        <>
          <Header>
            <Avatar name={name} size="md" />
            <HeaderText>
              <Text variant="body2" style={{ fontWeight: 600 }}>
                {name}
              </Text>
              <Muted>{member.email}</Muted>
              <Muted>
                {member.roleName ? `Role: ${member.roleName}` : 'No role'}
                {member.joinedDate
                  ? ` · Joined ${new Date(member.joinedDate).toLocaleDateString()}`
                  : ''}
              </Muted>
            </HeaderText>
          </Header>

          {error && <ErrorText role="alert">{error}</ErrorText>}

          <Section aria-label="Teams">
            <SectionTitle>Teams ({memberTeams.length})</SectionTitle>
            <Note>
              Teams and team roles decide which agents {name} can use. A deny on any of these
              teams or roles wins. Assign team roles from the team&apos;s drawer on the Teams tab.
            </Note>
            {memberTeams.map((team) => (
              <Row key={team.teamId}>
                <RowText>
                  <Text variant="body2" style={{ fontWeight: 500 }}>
                    {team.name}
                  </Text>
                  {team.isDefault && (
                    <Badge variant="info" size="sm">
                      Everyone
                    </Badge>
                  )}
                  {(team.roles || []).length > 0 && (
                    <RoleChips aria-label={`Team roles in ${team.name}`}>
                      {(team.roles || []).map((role) => (
                        <Badge key={role.roleId} variant="neutral" size="sm">
                          {role.name}
                        </Badge>
                      ))}
                    </RoleChips>
                  )}
                </RowText>
                <RowActions>
                  {team.isDefault ? (
                    <Badge variant={team.teamRole === 'owner' ? 'primary' : 'neutral'} size="sm">
                      {team.teamRole}
                    </Badge>
                  ) : (
                    <>
                      <Select
                        value={team.teamRole}
                        aria-label={`Role in ${team.name}`}
                        onChange={(e) =>
                          run(
                            () =>
                              updateTeamMember({
                                orgId,
                                teamId: team.teamId,
                                memberId: member.id,
                                teamRole: e.target.value as TeamRole,
                              }).unwrap(),
                            `Role in ${team.name} updated`,
                          )
                        }
                      >
                        <option value="owner">Owner</option>
                        <option value="member">Member</option>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove from ${team.name}`}
                        onClick={() =>
                          run(
                            () =>
                              removeTeamMember({
                                orgId,
                                teamId: team.teamId,
                                memberId: member.id,
                              }).unwrap(),
                            `Removed from ${team.name}`,
                          )
                        }
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </RowActions>
              </Row>
            ))}
            {joinable.length > 0 && (
              <Row>
                <Select
                  value={addTeamId}
                  aria-label="Team to join"
                  onChange={(e) => setAddTeamId(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Add to a team…</option>
                  {joinable.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!addTeamId || adding}
                  onClick={() => {
                    const teamName = joinable.find((t) => t.id === addTeamId)?.name || 'team';
                    run(async () => {
                      await addTeamMember({ orgId, teamId: addTeamId, memberId: member.id }).unwrap();
                      setAddTeamId('');
                    }, `Added to ${teamName}`);
                  }}
                >
                  Add to team
                </Button>
              </Row>
            )}
            <Note>Org team membership follows the organisation and cannot be changed here.</Note>
          </Section>
        </>
      )}
    </SlideOver>
  );
};

export default MemberDrawer;
